<template>
  <v-select
    v-model="value"
    :items="filteredList"
    :label="label"
    itemProps
    multiple
    fullWidth
    clearable
    data-cc="multi-select"
    class="bm-multi-select"
    :menuProps="{
      location: 'bottom'
    }"
    :listProps="computedListProps"
    @click="() => trackingEvents.openSelectInput(identifier)"
  >
    <template v-if="hasSearchBar" #prepend-item>
      <v-text-field
        v-model="search"
        variant="solo"
        density="compact"
        autofocus
        clearable
        hideDetails
        :placeholder="$t('generic.search')"
        autocomplete="off"
        class="multi-select-search-input bg-surface border-b-thin mb-2"
        data-cc="multi-select-search-field"
        @keydown.stop
      >
        <template #prepend>
          <v-icon icon="$search" class="ic-16" />
        </template>
      </v-text-field>
    </template>
    <template #selection="{ item, index }">
      <v-chip
        v-if="index === 0"
        :title="item.title"
        :class="{ 'selection-chip-short': value.length > 1 }"
        size="small"
        color="primary"
        variant="tonal"
        data-cc="multi-select-chip-text"
      >
        <span
          :class="{ 'selected-element-rtl': rtl }"
          class="selected-element text-truncate"
        >
          {{ item.title }}
        </span>
      </v-chip>
      <v-chip
        v-if="index === 1"
        :title="chipTitle"
        color="secondary"
        class="number-chip"
        data-cc="multi-select-chip-number"
      >
        <span> +{{ value.length - 1 }}</span>
      </v-chip>
    </template>
  </v-select>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { uniqBy } from 'lodash';
import trackingEvents from '@/config/trackingEvents';
import { logAppError } from '@/config/sentryConfig';

// Vuetify 3 item shapes
interface SelectItem {
  title: string;
  value: any;
}

type SelectItemInput = SelectItem | string;

interface Props {
  items?: SelectItemInput[];
  label?: string;
  rtl?: boolean;
  identifier?: string;
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  label: '',
  rtl: false,
  identifier: ''
});

const modelValue = defineModel<any[] | undefined>();

const search = ref('');
const showSearchMinNumberOfItems = 5;
const bringSelectedToTopLimit = 8;

const isObjectItem = (item: SelectItemInput): item is SelectItem =>
  typeof item === 'object' && item != null;

const normalizeItem = (item: SelectItemInput) => {
  if (!isObjectItem(item)) {
    return {
      title: String(item),
      value: item
    };
  }

  return item;
};

const hasSearchBar = computed(
  () => uniqueList.value.length >= showSearchMinNumberOfItems
);

const normalizedItems = computed(() =>
  props.items.map((item) => normalizeItem(item))
);

const selectMenuClass = computed(() => {
  const defaultClass = 'bm-multi-select-menu';
  const classes = [defaultClass];

  if (props.identifier) {
    classes.push(`${defaultClass}-${props.identifier}`);
  }

  // If we include the search bar in the menu,
  // we need to remove the padding to avoid the list item
  // being displayed behind the search bar
  if (hasSearchBar.value) {
    classes.push('py-0 pb-2');
  }

  return classes.join(' ');
});

// Add data attributes for testing
const customTestingAttribute = computed(() =>
  props.identifier ? `select-menu-${props.identifier}` : 'select-menu'
);

const computedListProps = computed(
  () =>
    ({
      class: selectMenuClass.value,
      density: 'compact',
      'data-cc': `list-${customTestingAttribute.value}`
    }) as Record<string, any>
);

// Vuetify 2 somehow removes duplicates from 'items' in template unlike Vuetify 3
// that's why we need to do it too but manually
const uniqueList = computed(() => uniqBy(normalizedItems.value, 'value'));

const value = computed({
  get: () => modelValue.value ?? [],
  set: (val: any[]) => {
    modelValue.value = val;
  }
});

const filteredList = computed(() => {
  if (!search.value) {
    if (uniqueList.value.length <= bringSelectedToTopLimit) {
      return uniqueList.value;
    }

    return uniqueList.value.toSorted(
      (a, b) => value.value.indexOf(b.value) - value.value.indexOf(a.value)
    );
  }

  return uniqueList.value.filter((item) => {
    // Not filtering already selected items because vuetify removes it from the input label
    return (
      item.title.toLowerCase().includes(search.value.toLowerCase()) ||
      value.value?.includes(item.value)
    );
  });
});

const chipTitle = computed(() => {
  const selected = uniqueList.value.filter((i) =>
    value.value?.includes(i.value)
  );
  if (selected.length) return selected.map((s) => s.title).join(', ');
  return '';
});

// Check for legacy label-only items
watch(
  () => props.items,
  (items) => {
    if (!items) return;

    items.forEach((item) => {
      if (
        typeof item === 'object' &&
        item !== null &&
        'label' in item &&
        !('title' in item)
      ) {
        logAppError({
          error: new Error('Legacy label-only item detected in BMMultiSelect'),
          functionName: 'BMMultiSelect',
          section: 'select',
          level: 'warning',
          message:
            "Item contains 'label' but not 'title'. Please migrate to Vuetify 3 format.",
          metadata: { item }
        });
      }
    });
  },
  { deep: true, immediate: true }
);
</script>

<style scoped lang="scss">
.bm-multi-select {
  :deep(.v-field__input) {
    flex-wrap: nowrap !important;
  }

  :deep(.v-select__selection) {
    flex: 0 0 auto;
    max-width: fit-content;

    &:first-of-type {
      flex: 0 1 auto;
      min-width: 0;
      overflow: hidden;
    }
  }

  :deep(.v-chip) {
    max-width: 100%;
    display: flex;
  }
}

.multi-select-search-input {
  position: sticky;
  top: 0;
  z-index: 2;

  :deep(.v-input__prepend) {
    margin-inline-end: 0;
    padding-inline-start: var(--bm-spacing-4);
  }

  :deep(.v-field__input) {
    padding-left: var(--bm-spacing-2);
  }
}

.number-chip {
  padding: 0 5px;
}

.selected-element {
  &-rtl {
    direction: rtl;
    text-align: left;
  }
}
</style>
